import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Wrench, Settings, Shield, CheckCircle, X } from 'lucide-react';
import BeforeAfterSlider from '../components/BeforeAfterSlider';
import SEO from '../components/SEO';
import { useContent } from '../hooks/useContent';

const RepairsPage = () => {
  const { content: heroImages } = useContent('heroImages');
  
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedService, setSelectedService] = useState('');
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
    service: ''
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const repairServices = [
    {
      title: "Engine Diagnostics & Repair",
      description: "Comprehensive MAN engine diagnostics and repair services using the latest MANCAT3 diagnostic equipment. Our MAN-certified technicians identify and resolve engine issues quickly.",
      features: ["Advanced MANCAT3 diagnostic scanning", "MAN engine rebuilds and overhauls", "MAN fuel system repair", "Emission system maintenance"],
      price: "Quote on request",
      icon: <Settings className="h-8 w-8 text-orange-600" />
    },
    {
      title: "ECU Remapping & Tuning",
      description: "Professional ECU remapping services to optimise your MAN truck's performance, fuel economy, and power delivery using genuine MAN protocols.",
      features: ["Performance tuning", "Economy remapping", "Power enhancement", "MAN-specific calibrations"],
      price: "£450 per vehicle",
      icon: <Settings className="h-8 w-8 text-orange-600" />
    },
    {
      title: "Parts Sales & Supply",
      description: "Comprehensive MAN parts supply service with genuine manufacturer and OEM parts. We supply and source used MAN truck parts of any kind to meet your requirements.",
      features: ["Genuine MAN manufacturer parts", "OEM quality replacements", "Full range of used MAN parts", "Custom fabrication work"],
      price: "Competitive pricing",
      icon: <Wrench className="h-8 w-8 text-orange-600" />
    },
    {
      title: "Transmission Services",
      description: "Expert MAN transmission repair and maintenance services specialising in automatic transmissions to keep your vehicles running smoothly and efficiently.",
      features: ["MAN automatic transmission repair", "Transmission rebuilds", "Clutch replacement", "Transmission fluid changes"],
      price: "Quote on request",
      icon: <Wrench className="h-8 w-8 text-orange-600" />
    },
    {
      title: "Brake System Maintenance",
      description: "Safety-critical MAN brake system services ensuring your commercial vehicles meet all safety standards and regulations with quarterly brake testing.",
      features: ["Brake pad and disc replacement", "Air brake system service", "Quarterly brake testing with load simulation", "Annual brake testing"],
      price: "Quote on request",
      icon: <Shield className="h-8 w-8 text-orange-600" />
    },
    {
      title: "Electrical System Repair",
      description: "Complete electrical system diagnostics and repair services for MAN trucks using MANCAT3 system with full dealership capabilities including programming and key services.",
      features: ["MAN MANCAT3 diagnostic system", "ECU programming and coding", "Speed limiter alterations", "Key programming services"],
      price: "Quote on request",
      icon: <CheckCircle className="h-8 w-8 text-orange-600" />
    },
    {
      title: "MOT Testing & Preparation",
      description: "Comprehensive MOT preparation services and testing arrangements. We provide thorough pre-MOT checks and can arrange testing through our network partners.",
      features: ["Pre-MOT inspection", "MOT preparation services", "Test arrangement", "Retest preparation"],
      price: "Quote on request",
      icon: <CheckCircle className="h-8 w-8 text-orange-600" />
    },
    {
      title: "Preventive Maintenance",
      description: "Scheduled MAN truck maintenance programmes designed to reduce downtime and extend vehicle life, keeping your fleet operational and compliant.",
      features: ["MAN service scheduling", "Oil and filter changes", "Tyre maintenance", "Fleet maintenance contracts"],
      price: "Quote on request",
      icon: <Clock className="h-8 w-8 text-orange-600" />
    },
    {
      title: "DVS/PSS Compliance Kits",
      description: "DVS/PSS systems installed and certified with certificate of conformity. Progressive Safe System compliance for HGVs travelling through Greater London. Applications carried out with Transport for London.",
      features: ["DVS/PSS system installation", "Certificate of conformity provided", "Transport for London applications", "Greater London compliance solutions"],
      price: "Quote on request",
      icon: <Shield className="h-8 w-8 text-orange-600" />
    }
  ];

  const handleServiceContact = (service) => {
    setSelectedService(service.title);
    setContactForm({
      name: '',
      email: '',
      phone: '',
      company: '',
      subject: `Enquiry about ${service.title}`,
      message: `Hi,\n\nI am interested in your ${service.title} service.\n\nService details:\n- ${service.description}\n- Price: ${service.price}\n\nPlease provide more information and availability.\n\nThanks`,
      service: service.title
    });
    setShowContactModal(true);
  };

  const handleContactFormChange = (e) => {
    setContactForm({
      ...contactForm,
      [e.target.name]: e.target.value
    });
  };

  const handleContactFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    setSubmitMessage('');

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/auth/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactForm)
      });

      if (response.ok) {
        setSubmitMessage('Your enquiry has been sent successfully! We will get back to you soon.');
        setContactForm({
          name: '',
          email: '',
          phone: '',
          company: '',
          subject: '',
          message: '',
          service: ''
        });
        // Close modal after 2 seconds
        setTimeout(() => {
          setShowContactModal(false);
          setSubmitMessage('');
        }, 2000);
      } else {
        throw new Error('Failed to send enquiry');
      }
    } catch (error) {
      console.error('Error sending enquiry:', error);
      setSubmitMessage('Failed to send enquiry. Please try again or contact us directly at sales@lawmancommercials.com');
    } finally {
      setSubmitLoading(false);
    }
  };

  const closeModal = () => {
    setShowContactModal(false);
    setSelectedService('');
    setSubmitMessage('');
  };

  const handleBookService = () => {
    setSelectedService('Book MAN Service');
    setContactForm({
      name: '',
      email: '',
      phone: '',
      company: '',
      subject: 'MAN Service Booking Request',
      message: 'Hi,\n\nI would like to book a service appointment for my MAN truck.\n\nPlease contact me to arrange a suitable time and discuss my requirements.\n\nThanks',
      service: 'Service Booking'
    });
    setShowContactModal(true);
  };

  const handleEmergencyContact = () => {
    window.location.href = 'tel:07718738785';
  };

  const handlePartsQuote = () => {
    setSelectedService('MAN Parts Quote');
    setContactForm({
      name: '',
      email: '',
      phone: '',
      company: '',
      subject: 'MAN Parts Quote Request',
      message: 'Hi,\n\nI would like to request a quote for MAN truck parts.\n\nPlease provide pricing and availability for the parts I need.\n\nThanks',
      service: 'Parts Quote'
    });
    setShowContactModal(true);
  };

  const handleUsedPartsEnquiry = () => {
    setSelectedService('Used Parts Enquiry');
    setContactForm({
      name: '',
      email: '',
      phone: '',
      company: '',
      subject: 'Used MAN Parts Enquiry',
      message: 'Hi,\n\nI am looking for used MAN truck parts.\n\nPlease let me know what you have available and pricing.\n\nThanks',
      service: 'Used Parts Enquiry'
    });
    setShowContactModal(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="MAN Truck Repairs & Services"
        description="Professional MAN truck repair services in Leicester. MANCAT3 diagnostics, engine repair, ECU remapping, transmission services, brakes, electrical. Up to 12 months coverage. Expert technicians with 30+ years experience."
        keywords="MAN truck repair Leicester, MANCAT3 diagnostics, truck engine repair, ECU remapping, MAN truck services, commercial vehicle repair"
        canonical="/repairs"
      />
      
      {/* Hero Section */}
      <section 
        className="relative bg-black text-white pt-32 pb-20 -mt-24"
        style={{
          backgroundImage: `url('${heroImages?.repairsPage || 'https://customer-assets.emergentagent.com/job_commercials-admin/artifacts/6vbgligq_2.%20Repairs.webp'}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-black/30"></div>
        
        {/* Enhanced black overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/40"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 font-inter">
            Professional MAN Truck Services
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto font-inter">
            Expert MAN truck repair, maintenance, and parts supply services. Specialising in MAN trucks with MANCAT3 diagnostic capabilities and genuine parts supply.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-3 rounded font-bold transition-colors shadow-lg"
              onClick={handleBookService}
            >
              Book MAN Service
            </button>
            <button 
              className="border-2 border-white text-white hover:bg-white hover:text-black px-8 py-3 rounded font-bold transition-colors shadow-lg"
              onClick={handleEmergencyContact}
            >
              Emergency Contact
            </button>
          </div>
        </div>
      </section>

      {/* Before & After Showcase */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4 font-inter">
              See Our <span className="text-orange-600">Quality Work</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto font-inter">
              Our expert technicians transform damaged MAN trucks back to peak condition. 
              See a real example of our professional repair work.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Before/After Slider */}
            <div className="order-2 lg:order-1">
              <BeforeAfterSlider
                beforeImage="https://customer-assets.emergentagent.com/job_lawman-truck-fix/artifacts/cslfmvg3_1000012599.webp"
                afterImage="https://customer-assets.emergentagent.com/job_lawman-truck-fix/artifacts/yrrt02f4_PXL_20240704_102321122.webp"
                beforeLabel="Before Repair"
                afterLabel="After Repair"
                className=""
              />
            </div>
            
            {/* Right Column - Text Content */}
            <div className="order-1 lg:order-2">
              <h3 className="text-2xl font-bold text-black mb-6 font-inter">
                Complete MAN Truck Restoration
              </h3>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <div className="bg-orange-600 rounded-full w-8 h-8 flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                    <Settings className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">Engine Overhaul</h4>
                    <p className="text-gray-600">Complete engine rebuild using genuine MAN parts and MANCAT3 diagnostics for optimal performance.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-orange-600 rounded-full w-8 h-8 flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                    <Shield className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">Body Restoration</h4>
                    <p className="text-gray-600">Professional bodywork repair, panel replacement, and premium paint finishing to factory standards.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-orange-600 rounded-full w-8 h-8 flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                    <CheckCircle className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">Quality Assurance</h4>
                    <p className="text-gray-600">Comprehensive testing and up to 12 months coverage on all repair work to ensure long-lasting results.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-orange-600 rounded-full w-8 h-8 flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                    <Wrench className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">Expert Technicians</h4>
                    <p className="text-gray-600">MAN-certified mechanics with 30+ years experience in commercial vehicle restoration.</p>
                  </div>
                </div>
              </div>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="mailto:Sales@lawmancommercials.com?subject=Repair Service Enquiry"
                  className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 rounded font-bold text-lg transition-colors flex items-center justify-center"
                >
                  <Mail className="mr-2 h-5 w-5" />
                  Get Repair Quote
                </a>
                <a
                  href="tel:01530231235"
                  className="bg-black hover:bg-gray-800 text-white px-8 py-4 rounded font-bold text-lg transition-colors flex items-center justify-center"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Call Now
                </a>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 mt-8 pt-8 border-t border-gray-200">
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">100+</div>
                  <div className="text-sm text-gray-600">Trucks Restored</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">30+</div>
                  <div className="text-sm text-gray-600">Years Experience</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4 font-inter">
              Comprehensive MAN Truck Services
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto font-inter">
              From routine MAN maintenance to complex repairs and ECU remapping - our experienced MAN-certified team provides comprehensive services to keep your MAN trucks operating at peak performance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {repairServices.map((service, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-4">
                  {service.icon}
                  <h3 className="text-xl font-bold text-black ml-3 font-inter">{service.title}</h3>
                </div>
                
                <p className="text-gray-600 mb-4 font-inter">
                  {service.description}
                </p>
                
                <ul className="space-y-2 mb-4">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-sm text-gray-700">
                      <CheckCircle className="h-4 w-4 text-green-600 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-black">{service.price}</span>
                  <button 
                    className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded font-medium transition-colors"
                    onClick={() => handleServiceContact(service)}
                  >
                    Contact Us
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Parts & Bespoke Builds Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Parts Supply */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-black mb-6 font-inter">
                MAN Parts Supply & Used Parts
              </h2>
              <p className="text-lg text-gray-600 mb-6 font-inter">
                Complete MAN parts supply service with genuine manufacturer and OEM quality parts. We supply and source used MAN truck parts of any kind to meet your specific requirements.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div className="ml-3">
                    <h4 className="font-bold text-black">Genuine MAN Parts</h4>
                    <p className="text-gray-600">Extensive inventory of genuine MAN manufacturer parts and OEM quality replacements</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div className="ml-3">
                    <h4 className="font-bold text-black">Used MAN Parts Supply</h4>
                    <p className="text-gray-600">Full range of quality used MAN truck parts available through our supply and sourcing network including engines, gearboxes, axles, and body panels</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div className="ml-3">
                    <h4 className="font-bold text-black">MAN Specialist Knowledge</h4>
                    <p className="text-gray-600">30+ years expertise in MAN trucks ensures we source and supply the correct parts for your specific model and application</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div className="ml-3">
                    <h4 className="font-bold text-black">Custom Fabrication</h4>
                    <p className="text-gray-600">In-house fabrication services for custom MAN truck components and modifications</p>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded font-bold transition-colors"
                  onClick={handlePartsQuote}
                >
                  Request MAN Parts Quote
                </button>
                <button 
                  className="border-2 border-orange-600 text-orange-600 hover:bg-orange-600 hover:text-white px-6 py-3 rounded font-bold transition-colors"
                  onClick={handleUsedPartsEnquiry}
                >
                  Used Parts Enquiry
                </button>
              </div>
            </div>
            
            {/* Right Column - Stats */}
            <div className="bg-gray-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-black mb-6 font-inter">Why Choose Our MAN Parts Service?</h3>
              
              <div className="space-y-6">
                <div className="flex items-center">
                  <div className="bg-orange-100 rounded-full w-12 h-12 flex items-center justify-center mr-4">
                    <Shield className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-black">Up to 12 Month</div>
                    <div className="text-sm text-gray-600">MAN Parts Coverage</div>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="bg-orange-100 rounded-full w-12 h-12 flex items-center justify-center mr-4">
                    <Clock className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-black">24 Hour</div>
                    <div className="text-sm text-gray-600">Parts Delivery</div>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="bg-orange-100 rounded-full w-12 h-12 flex items-center justify-center mr-4">
                    <Wrench className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-black">MAN Specialist</div>
                    <div className="text-sm text-gray-600">Parts Knowledge</div>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="bg-orange-100 rounded-full w-12 h-12 flex items-center justify-center mr-4">
                    <Settings className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-black">30+ Years</div>
                    <div className="text-sm text-gray-600">MAN Experience</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Services */}
      <section className="py-16" style={{ backgroundColor: '#3D3D3D' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 font-inter">
              24/7 Emergency Repair Services
            </h2>
            <p className="text-lg mb-8 max-w-3xl mx-auto font-inter">
              Vehicle breakdown? Don't let unexpected repairs disrupt your business. Our emergency repair team is available round the clock to get you back on the road.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              <div className="text-center">
                <Clock className="h-12 w-12 text-orange-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">24/7 Availability</h3>
                <p className="text-gray-300">Round-the-clock emergency repair services</p>
              </div>
              <div className="text-center">
                <Phone className="h-12 w-12 text-orange-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">Rapid Response</h3>
                <p className="text-gray-300">Average response time under 2 hours</p>
              </div>
              <div className="text-center">
                <Wrench className="h-12 w-12 text-orange-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">Mobile Repairs</h3>
                <p className="text-gray-300">On-site repair services available</p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:07718738785"
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded font-bold transition-colors inline-flex items-center justify-center"
              >
                <Phone className="h-5 w-5 mr-2" />
                Emergency Contact: 07718 738 785
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4 font-inter">
              Visit Our Service Centre
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto font-inter">
              State-of-the-art facilities with experienced technicians ready to service your commercial vehicles.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="space-y-6">
              <div className="flex items-start">
                <MapPin className="h-6 w-6 text-orange-600 mt-1 flex-shrink-0" />
                <div className="ml-4">
                  <h3 className="text-lg font-bold text-black mb-1">Service Centre Address</h3>
                  <p className="text-gray-600">
                    Lawman Commercials<br />
                    31 Merrylees Industrial Estate<br />
                    Leeside, Desford, Leicester LE9 9FS<br />
                    United Kingdom
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <Phone className="h-6 w-6 text-orange-600 mt-1 flex-shrink-0" />
                <div className="ml-4">
                  <h3 className="text-lg font-bold text-black mb-1">Service Booking</h3>
                  <p className="text-gray-600">
                    Phone: <a href="tel:01530231235" className="text-orange-600 hover:text-orange-700">01530 231 235</a><br />
                    Emergency: <a href="tel:07718738785" className="text-red-600 hover:text-red-700">07718 738 785</a>
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <Mail className="h-6 w-6 text-orange-600 mt-1 flex-shrink-0" />
                <div className="ml-4">
                  <h3 className="text-lg font-bold text-black mb-1">Email Contact</h3>
                  <p className="text-gray-600">
                    Service: <a href="mailto:sales@lawmancommercials.com" className="text-orange-600 hover:text-orange-700">sales@lawmancommercials.com</a><br />
                    Parts: <a href="mailto:Sales@lawmancommercials.com" className="text-orange-600 hover:text-orange-700">Sales@lawmancommercials.com</a>
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <Clock className="h-6 w-6 text-orange-600 mt-1 flex-shrink-0" />
                <div className="ml-4">
                  <h3 className="text-lg font-bold text-black mb-1">Opening Hours</h3>
                  <div className="text-gray-600">
                    <p>Mon-Fri: 8:00am – 5:30pm</p>
                    <p>Sat: 8:00am – 12:00pm</p>
                    <p>Sun: Closed</p>
                    <p className="text-red-600 font-medium mt-2">24/7 Emergency Service Available</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Service Request Form */}
            <div className="bg-gray-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-black mb-6 font-inter">Book a Service</h3>
              <form className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Name *</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Phone *</label>
                    <input
                      type="tel"
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Vehicle Registration</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="e.g. AB12 CDE"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Service Required</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent">
                    <option value="">Select service type</option>
                    <option value="engine">Engine Diagnostics & Repair</option>
                    <option value="transmission">Transmission Services</option>
                    <option value="brakes">Brake System Maintenance</option>
                    <option value="electrical">Electrical System Repair</option>
                    <option value="mot">MOT Testing</option>
                    <option value="maintenance">Preventive Maintenance</option>
                    <option value="remapping">ECU Remapping & Tuning</option>
                    <option value="parts">Parts Supply & Ordering</option>
                    <option value="bespoke">Bespoke Truck Build</option>
                    <option value="breaking">Vehicle Breaking & Parts</option>
                    <option value="emergency">Emergency Repair</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Message</label>
                  <textarea
                    rows="4"
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Please describe the issue or service requirements..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded font-bold transition-colors"
                >
                  Request Service Booking
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
      
      {/* Service Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-black">Service Enquiry</h2>
                <button 
                  onClick={closeModal}
                  className="text-gray-500 hover:text-gray-700 p-1"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {selectedService && (
                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                  <h3 className="font-bold text-lg text-black mb-2">Selected Service</h3>
                  <p className="text-gray-600">{selectedService}</p>
                </div>
              )}

              {submitMessage && (
                <div className={`p-4 rounded-lg mb-4 ${
                  submitMessage.includes('successfully') 
                    ? 'bg-green-50 text-green-800 border border-green-200' 
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  {submitMessage}
                </div>
              )}

              <form onSubmit={handleContactFormSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={contactForm.name}
                      onChange={handleContactFormChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={contactForm.email}
                      onChange={handleContactFormChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={contactForm.phone}
                      onChange={handleContactFormChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={contactForm.company}
                      onChange={handleContactFormChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Service Required *
                  </label>
                  <select
                    name="service"
                    value={contactForm.service}
                    onChange={handleContactFormChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    <option value="">Select a service</option>
                    <option value="Engine Diagnostics & Repair">Engine Diagnostics & Repair</option>
                    <option value="ECU Remapping & Tuning">ECU Remapping & Tuning</option>
                    <option value="Parts Sales & Supply">Parts Sales & Supply</option>
                    <option value="Transmission Services">Transmission Services</option>
                    <option value="Brake System Maintenance">Brake System Maintenance</option>
                    <option value="Electrical System Repair">Electrical System Repair</option>
                    <option value="MOT Testing & Preparation">MOT Testing & Preparation</option>
                    <option value="Preventive Maintenance">Preventive Maintenance</option>
                    <option value="Custom Service">Other/Custom Service</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={contactForm.subject}
                    onChange={handleContactFormChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={contactForm.message}
                    onChange={handleContactFormChange}
                    required
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={submitLoading}
                    className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-2 rounded font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitLoading ? 'Sending...' : 'Send Enquiry'}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-2 rounded font-bold transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RepairsPage;